import React, { useEffect, useState } from 'react';
import { Form, Input, message } from 'antd';
import { auth, signInWithEmailAndPassword, db } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from "firebase/auth";

const Login = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  const handleRefresh = () => {
    navigate('/profile');
    window.location.reload();
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDoc = await getDoc(doc(db, 'members', currentUser.uid));
        if (userDoc.exists()) setUserData(userDoc.data());
      } else {
        setUser(null);
        setUserData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      message.success('Login successful!');
      form.resetFields();
      setUser(auth.currentUser);
    } catch (error) {
      if (error.code === 'auth/user-disabled') {
        message.error(
          'This login was a duplicate registration and has been merged into your main member account. ' +
          'Please sign in with the other email address you registered with, or contact the BCS ICT office.',
          8
        );
      } else {
        message.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      message.success('Logged out successfully');
      setUser(null);
      setUserData(null);
    } catch (error) {
      message.error('Failed to log out');
    }
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, values.email);
      setResetMessage("Reset email sent. Check your inbox.");
    } catch (error) {
      setResetMessage("Error: " + error.message);
    }
    setLoading(false);
  };

  const inputClass = "h-11 rounded-none border-0 border-b border-gray-300 bg-transparent px-0 text-sm focus:border-blue-600 hover:border-blue-400";

  /* ── Logged-in view ── */
  if (user) {
    return (
      <div className="min-h-screen bg-[#ffff] pt-36 pb-20 px-4">
        <div className="max-w-lg mx-auto">

          {/* Profile card */}
          <div className="bg-white border border-gray-100 rounded-sm overflow-hidden">

            {/* Top banner + avatar */}
            <div className="h-24 bg-gradient-to-r from-blue-600 to-blue-500 relative">
              <div className="absolute -bottom-10 left-8">
                <div className="w-20 h-20 rounded-full border-4 border-white overflow-hidden bg-gray-100">
                  {userData?.profile_image ? (
                    <img src={userData.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl font-black text-gray-300">
                      {userData?.first_name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Name row */}
            <div className="pt-14 px-8 pb-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {`${userData?.first_name || ''} ${userData?.middle_name || ''} ${userData?.last_name || ''}`.trim() || 'Member'}
              </h2>
              <div className="flex flex-wrap gap-2 mt-2">
                {userData?.pew && (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-sm">
                    {userData.pew}
                  </span>
                )}
                {userData?.bethel && (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-sm">
                    {userData.bethel}
                  </span>
                )}
              </div>
            </div>

            {/* Info rows */}
            <div className="px-8 py-6 space-y-5">

              {[
                { label: "Email Address", value: user.email },
                { label: "Phone Number", value: userData?.phone_number },
                { label: "State of Origin", value: userData?.state_of_origin },
              ].map((row) => (
                <div key={row.label}>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-1">
                    {row.label}
                  </p>
                  <p className="text-sm font-medium text-gray-800 border-b border-gray-100 pb-2">
                    {row.value || <span className="text-gray-300 italic">Not set</span>}
                  </p>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="px-8 pb-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/profile"
                onClick={handleRefresh}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase px-6 py-3 rounded-sm transition-all duration-200"
              >
                Edit Profile
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <button
                onClick={handleLogout}
                className="flex-1 flex items-center justify-center gap-2 border border-red-200 text-red-500 hover:bg-red-50 text-xs font-bold tracking-widest uppercase px-6 py-3 rounded-sm transition-all duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Logged-out view ── */
  return (
    <div className="min-h-screen bg-[#fff] flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="block w-7 h-px bg-blue-600" />
            <span className="text-blue-600 text-[10px] font-bold tracking-widest uppercase">
              BCS Member Portal
            </span>
            <span className="block w-7 h-px bg-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isResetPassword ? "Reset Password" : "Welcome Back"}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {isResetPassword
              ? "Enter your email to receive a reset link"
              : "Sign in to your BCS member account"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-100 rounded-sm px-8 py-10">

          {!isResetPassword ? (
            /* ── Login form ── */
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item
                name="email"
                label={<span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">Email Address</span>}
                rules={[{ required: true, message: "Enter your email" }]}
              >
                <Input className={inputClass} placeholder="you@example.com" />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">Password</span>}
                rules={[{ required: true, message: "Enter your password" }]}
                className="mt-6"
              >
                <Input.Password className={inputClass} placeholder="••••••••" />
              </Form.Item>

              {/* Forgot password */}
              <div className="flex justify-end mt-2 mb-8">
                <button
                  type="button"
                  onClick={() => setIsResetPassword(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase py-3 rounded-sm transition-all duration-200 disabled:opacity-50"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </Form>

          ) : (
            /* ── Reset password form ── */
            <Form form={form} layout="vertical" onFinish={handleResetPassword}>
              <Form.Item
                name="email"
                label={<span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">Email Address</span>}
                rules={[{ required: true, message: "Enter your email" }]}
              >
                <Input className={inputClass} placeholder="you@example.com" />
              </Form.Item>

              <div className="mt-8 space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase py-3 rounded-sm transition-all duration-200 disabled:opacity-50"
                >
                  {loading ? "Sending..." : "Send Reset Email"}
                </button>

                <button
                  type="button"
                  onClick={() => { setIsResetPassword(false); setResetMessage(""); }}
                  className="w-full border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs font-bold tracking-widest uppercase py-3 rounded-sm transition-all duration-200"
                >
                  Back to Sign In
                </button>
              </div>

              {resetMessage && (
                <p className={`text-xs mt-4 text-center font-medium ${resetMessage.startsWith("Error") ? "text-red-500" : "text-green-600"}`}>
                  {resetMessage}
                </p>
              )}
            </Form>
          )}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Not yet registered?{" "}
          <Link to="/register" className="text-blue-600 font-semibold hover:text-blue-700">
            Create an account
          </Link>
        </p>

      </div>
    </div>
  );
};

export default Login;